import { Combobox, ComboboxInput, ComboboxPopover } from '@reach/combobox';
import { useField } from 'formik';
import React__default from 'react';
import styled from 'styled-components';
import { setTestId } from '../../lib/testId.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { DEPRECATED_ComboboxList } from '../inputs/DEPRECATED_ComboboxCommon.js';
import { InnerInputLabel } from '../inputs/InputCommon.js';
import { Stack } from '../layout/Stack.js';
import { CSSGenerateTextFieldError } from '../styles/cssGenerateField.js';
import { setPortalLayer } from '../styles/stylePropTypes.js';
import { CSSDefaultInputField } from './common.js';
import { useSearchComboboxOptions } from './DEPRECATED_commonCombobox.js';
import { hasError } from './Field.js';
import { FIELD_ERROR_DATA_ATTRIBUTE, setErrorDataAttribute, FieldErrorMessage } from './FieldError.js';

const MIN_INPUT_WIDTH_REM = 13;
const StyledCombobox = styled(Combobox)`
  position: relative;
`;
const StyledComboboxInput = styled(ComboboxInput)`
  &[data-reach-combobox-input] {
    ${CSSDefaultInputField}
    position: relative;
    display: flex;
    cursor: pointer;
    padding: 1.5rem 2.5rem 0.5rem 1rem;
    align-items: flex-start;
    width: 100%;
    min-width: ${MIN_INPUT_WIDTH_REM}rem;

    [${FIELD_ERROR_DATA_ATTRIBUTE}] > [data-reach-combobox] &,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > [data-reach-combobox] &:hover,
    [${FIELD_ERROR_DATA_ATTRIBUTE}] > [data-reach-combobox] &:focus {
      ${CSSGenerateTextFieldError()}
      color: inherit;
    }
  }

  [data-reach-combobox] &[data-reach-combobox-input][aria-expanded='true'],
  [data-reach-combobox] &[data-reach-combobox-input][aria-expanded='true']:hover,
  [data-reach-combobox] &[data-reach-combobox-input]:focus,
  [data-reach-combobox] &[data-reach-combobox-input][aria-expanded='true']:focus {
    outline: none;
    border-color: ${COLOR_PALETTE.slateGrey40};
  }

  &[data-reach-combobox-input][aria-expanded='true'] ~ [data-reach-listbox-arrow] {
    transform: rotate(180deg);
  }
`;
const StyledListBoxArrow = styled.span`
  position: absolute;
  top: 50%;
  margin-top: -2px;
  right: 1rem;
  display: block;
  width: 0px;
  height: 0px;
  content: '';
  transform: rotate(0deg);
  border-top: 5px solid ${COLOR_PALETTE.slateGrey100};
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  transition: transform 0.2s ease 0s, -webkit-transform 0.2s ease 0s;
  pointer-events: none;
`;
const StyledComboboxPopover = styled(ComboboxPopover)`
  &[data-reach-combobox-popover] {
    background-color: white;
    box-shadow: rgba(0, 0, 0, 0.16) 0px 2px 3px 0px;
    border: 1px solid ${COLOR_PALETTE.slateGrey40};
    border-radius: 0.25rem;
    padding: 0;
    ${setPortalLayer()}
  }

  &[data-reach-combobox-popover],
  &[data-reach-combobox-popover]:focus-within {
    box-shadow: rgba(0, 0, 0, 0.16) 0px 2px 3px 0px;
    outline: none;
  }

  &[data-reach-combobox-popover] [data-reach-combobox-list] {
    max-height: 14rem;
    overflow: auto;
  }
`;
/**
 * Select Input with Formik Field bindings.
 * Accepts `ComboboxOptions` as child components. If a 'deselected' option is required, it
 * must be manually added to options.
 *
 * @deprecated - use `SelectInput` with an onSearch handler instead
 *
 * @example
 * <ComboboxField name="chicken">
 *  <ComboboxOption label="" value={SELECT_OPTION_NO_VALUE} />
 *  <ComboboxOption label="Egg" value="egg" />
 *  <ComboboxOption label="Duck" value="duck" />
 * </ComboboxField>
 */ const DEPRECATED_ComboboxField = ({ name , label , children , testId ="X" , reserveMessageSpace =true , onFieldChange , ...props })=>{
    const [field, meta, helpers] = useField(name);
    const { setValue , setTouched  } = helpers;
    const { filteredChildren , handleChange , handleSearchTermChange , handleBlur , searchTotalCount , searchTerm  } = useSearchComboboxOptions(children, field, setValue, setTouched, onFieldChange);
    return React__default.createElement(Stack, {
        "data-hpy-web-combobox-field": true,
        ...props,
        ...setTestId("ComboboxField-" + testId),
        ...setErrorDataAttribute(hasError(meta)),
        gap: 0.25
    }, React__default.createElement(StyledCombobox, {
        openOnFocus: true,
        onSelect: handleChange
    }, React__default.createElement(StyledComboboxInput, {
        "data-hpy-web-combobox-field-input": true,
        ...setTestId("ComboboxField-ComboboxInput-" + testId),
        placeholder: label,
        value: searchTerm,
        selectOnClick: true,
        onChange: handleSearchTermChange,
        onBlur: handleBlur
    }), React__default.createElement(InnerInputLabel, {
        "data-hpy-web-combobox-input-input-inner-label": true,
        size: "small"
    }, label), React__default.createElement(StyledListBoxArrow, null, "\xa0"), React__default.createElement(StyledComboboxPopover, {
        portal: true
    }, React__default.createElement(DEPRECATED_ComboboxList, {
        totalOptions: searchTotalCount
    }, filteredChildren))), reserveMessageSpace && React__default.createElement(FieldErrorMessage, {
        name: name,
        reserveMessageSpace: reserveMessageSpace
    }));
};
DEPRECATED_ComboboxField.dataId = "data-hpy-web-combobox-field";
DEPRECATED_ComboboxField.displayName = "ComboboxField";

export { DEPRECATED_ComboboxField };
